/**
 * ThemeBadge component with built-in lazy loading of theme colours
 * Autonomous component that fetches theme enrichment data internally
 * Can be used in any React app without external colour service
 */

'use client';

import { X } from 'lucide-react';
import { useThemeColorEnrichment } from '../hooks';
import { genColorHex } from '../utils/color';

export interface ThemeBadgeTheme {
  id: string;
  name: string;
  color?: string | null;
}

export interface ThemeBadgeProps {
  /** Theme data with id, name, and optional colour */
  theme: ThemeBadgeTheme;
  /** Variant style for the badge */
  variant?: 'default' | 'compact' | 'filter';
  /** Whether the badge is removable (shows X button) */
  removable?: boolean;
  /** Callback when remove button is clicked */
  onRemove?: (themeId: string) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * ThemeBadge displays a single theme with coloured border
 * Automatically fetches theme colour if not provided
 *
 * @example
 * ```tsx
 * <ThemeBadge
 *   theme={{ id: '1', name: 'Rail Freight Forward', color: '#2B5F9E' }}
 *   variant="default"
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Without colour - will be fetched automatically
 * <ThemeBadge
 *   theme={{ id: '1', name: 'Green Logistics' }}
 *   variant="filter"
 *   removable={true}
 *   onRemove={(id) => console.log('Remove theme:', id)}
 * />
 * ```
 */
export function ThemeBadge({
  theme,
  variant = 'default',
  removable = false,
  onRemove,
  className = '',
}: ThemeBadgeProps) {
  const hasProvidedColor = Boolean(theme.color);
  const hasProvidedName = Boolean(theme.name && theme.name.trim() !== '');
  const enrichedData = useThemeColorEnrichment(theme.id, hasProvidedColor, hasProvidedName);

  // Use provided values first, then enriched values
  const finalColor = theme.color || enrichedData.color;
  const finalName = theme.name || enrichedData.name;
  const isLoading = enrichedData.isLoading && !hasProvidedName && !finalName;

  const baseClasses =
    'inline-flex items-center gap-1 rounded-full border border-transparent transition-all duration-300';

  const variantClasses = {
    default: 'px-2 py-0.5 text-xs font-medium text-primary',
    compact: 'px-1.5 py-0.5 text-xs font-medium text-primary',
    filter: 'px-2 py-0.5 text-xs font-medium text-primary',
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove(theme.id);
    }
  };

  // Skeleton component for loading state
  const Skeleton = ({ width = 'w-20' }: { width?: string }) => (
    <span
      className={`inline-block ${width} h-4 bg-accent/50 animate-pulse rounded`}
      aria-hidden="true"
    />
  );

  // Generate background colour with slight lightening and low opacity
  const backgroundColor = finalColor
    ? genColorHex(finalColor, { adjust: +20, opacity: 0.2 })
    : 'transparent';

  return (
    <span
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={{
        backgroundColor,
      }}
    >
      {isLoading ? <Skeleton width={variant === 'compact' ? 'w-16' : 'w-20'} /> : finalName || ''}
      {removable && onRemove && !isLoading && (
        <span
          onClick={handleRemove}
          className="hover:bg-primary/20 rounded-full cursor-pointer inline-flex"
          role="button"
          aria-label={`Remove ${finalName || ''}`}
        >
          <X className="h-3 w-3" />
        </span>
      )}
    </span>
  );
}
